// ── teamColors — shared "winner = green" color picker ──────────────
//
// The score headers (GameScoreHeader / CompactGameScoreHeader, via
// gameScoreModel) color a team's name the same way. Deriving the choice
// here keeps them in lockstep when the leader flips mid-game (live
// consensus crosses 50¢) or when final scores resolve.
//
// Convention:
//   • WINNER → green (positive)
//   • LOSER  → blue
//   • NULL (no clear leader) → fall back to the historical home-green /
//     away-blue palette so the chart isn't suddenly two gray lines
//     during the pre-snapshot window.
//
// "Winner" definition:
//   • Final game (statusId === 3) → side with the higher score
//   • Live or pre-game → side whose `awayFairProbCents` > 50 (consensus
//     is the market's belief about the ultimate winner; scores in early
//     innings/quarters are noisy)
//   • Tied or missing data → null

import type {
  OddsStreamConsensus,
  OddsStreamGameState,
} from '../../services/oddsStream';
import { isConfirmedFinal } from '@/lib/sport/periodLabel';

export const WINNER_COLOR = '#22c55e'; // green-500
export const LOSER_COLOR = '#3b82f6'; // blue-500
/** Middle / tied outcome in a 3-way rank coloring — gray-400. */
export const MIDDLE_COLOR = '#9ca3af';
// Historical fallback when no winner can be derived — keeps the chart
// from going monochrome during the pre-snapshot connect window.
const DEFAULT_AWAY_COLOR = '#3b82f6';
const DEFAULT_HOME_COLOR = '#22c55e';

export type TeamSide = 'away' | 'home';

export function deriveWinnerSide(
  latestConsensus: OddsStreamConsensus | null,
  gameState: OddsStreamGameState | null,
): TeamSide | null {
  // Only a CONFIRMED final (statusId 3 with the score) claims the decision;
  // a scoreless "final" is a stale gameState record (live soccer,
  // 2026-06-12) and falls through to the consensus leader below.
  if (isConfirmedFinal(gameState)) {
    const a = gameState!.awayScore as number;
    const h = gameState!.homeScore as number;
    if (a > h) return 'away';
    if (h > a) return 'home';
    return null; // drawn final — no winner to color
  }
  if (latestConsensus) {
    if (latestConsensus.awayFairProbCents > 50) return 'away';
    if (latestConsensus.homeFairProbCents > 50) return 'home';
  }
  return null;
}

export function colorForSide(side: TeamSide, winnerSide: TeamSide | null): string {
  if (winnerSide == null) {
    return side === 'away' ? DEFAULT_AWAY_COLOR : DEFAULT_HOME_COLOR;
  }
  return side === winnerSide ? WINNER_COLOR : LOSER_COLOR;
}

/** Rank-based color across a 3-way outcome set (Dan, Jun 11): highest fair
 *  prob = green, lowest = blue (same green-leader/blue-trailer read as the
 *  binary winner/loser convention above), middle or any tie = grey. Used
 *  by the score headers whenever drawFairProbCents is
 *  present; binary games keep deriveWinnerSide/colorForSide, which also
 *  handles final-score resolution and the pre-snapshot fallback. */
export function rankColor(value: number, all: number[]): string {
  if (all.filter((v) => v === value).length >= 2) return MIDDLE_COLOR;
  const distinct = [...new Set(all)].sort((a, b) => b - a);
  const rank = distinct.indexOf(value);
  if (rank === 0) return WINNER_COLOR;
  if (rank === distinct.length - 1) return LOSER_COLOR;
  return MIDDLE_COLOR;
}
